import ExcelJS from 'exceljs';

/**
 * Format currency for Excel display
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/**
 * Format percentage for Excel display
 */
const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value}%`;
};

/**
 * Get current date string for filename
 */
const getDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Style constants
 */
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
const HEADER_ALIGNMENT = { horizontal: 'center', vertical: 'middle' };
const CELL_BORDER = {
  top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
  left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
  bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
  right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
};

/**
 * Add a styled table to a worksheet
 */
const addStyledTable = (worksheet, data, headers, startRow = 1) => {
  if (!data || data.length === 0) {
    worksheet.getCell(startRow, 1).value = 'No data available';
    return startRow + 1;
  }

  // Add header row
  const headerRow = worksheet.getRow(startRow);
  headers.forEach((header, colIndex) => {
    const cell = headerRow.getCell(colIndex + 1);
    cell.value = header;
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = HEADER_ALIGNMENT;
    cell.border = CELL_BORDER;
  });
  headerRow.height = 22;

  // Add data rows
  data.forEach((rowData, rowIndex) => {
    const row = worksheet.getRow(startRow + rowIndex + 1);
    headers.forEach((header, colIndex) => {
      const cell = row.getCell(colIndex + 1);
      cell.value = rowData[header];
      cell.border = CELL_BORDER;
      cell.alignment = { vertical: 'middle' };
    });
  });

  // Auto-fit columns
  headers.forEach((header, colIndex) => {
    const column = worksheet.getColumn(colIndex + 1);
    let maxLength = header.length;
    data.forEach(row => {
      const cellValue = String(row[header] || '');
      maxLength = Math.max(maxLength, cellValue.length);
    });
    column.width = Math.min(Math.max(maxLength + 2, 10), 30);
  });

  return startRow + data.length + 1;
};

/**
 * Download workbook as file
 */
const downloadWorkbook = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Export Manual Mode results to Excel
 * @param {Object} results - The calculation results
 * @param {Object} formData - The form input data (property, params, valuation)
 * @param {boolean} includeInputs - Whether to include input data sheet
 */
export const exportManualModeResults = async (results, formData, includeInputs = false) => {
  if (!results) {
    console.error('No results to export');
    return;
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'PropNinja';
  workbook.created = new Date();

  // Sheet 1: Cash Flow
  if (results.cashFlow && results.cashFlow.length > 0) {
    const ws = workbook.addWorksheet('Cash Flow');
    const headers = ['Year', 'Gross Revenue', 'Vacancy Loss', 'EGR', 'Operating Expenses', 'NOI', 'Principal', 'Interest', 'Cash Flow'];
    const data = results.cashFlow.map(row => ({
      'Year': row.Year,
      'Gross Revenue': formatCurrency(row['Potential Gross Revenue']),
      'Vacancy Loss': formatCurrency(row['Vacancy Loss']),
      'EGR': formatCurrency(row['Effective Gross Revenue']),
      'Operating Expenses': formatCurrency(row['Operating Expenses']),
      'NOI': formatCurrency(row['NOI']),
      'Principal': formatCurrency(row['Principal Payment']),
      'Interest': formatCurrency(row['Interest Payment']),
      'Cash Flow': formatCurrency(row['Cash Flow'])
    }));
    addStyledTable(ws, data, headers);
  }

  // Sheet 2: Amortization
  if (results.amortization && results.amortization.length > 0) {
    const ws = workbook.addWorksheet('Amortization');
    const headers = ['Year', 'Principal', 'Interest', 'Total Payment', 'Remaining Balance'];
    const data = results.amortization.map(row => ({
      'Year': row.Year,
      'Principal': formatCurrency(row['Principal Payment']),
      'Interest': formatCurrency(row['Interest Payment']),
      'Total Payment': formatCurrency(row['Principal Payment'] + row['Interest Payment']),
      'Remaining Balance': formatCurrency(row['Remaining Balance'])
    }));
    addStyledTable(ws, data, headers);
  }

  // Sheet 3: IRR
  if (results.annual_irrs && results.annual_irrs.length > 0) {
    const ws = workbook.addWorksheet('IRR');
    const headers = ['Year', 'IRR'];
    const data = results.annual_irrs.map(row => ({
      'Year': row.Year,
      'IRR': row.IRR !== null ? formatPercent(row.IRR) : 'N/A'
    }));
    addStyledTable(ws, data, headers);
  }

  // Sheet 4: COC
  if (results.coc && results.coc.length > 0) {
    const ws = workbook.addWorksheet('COC');
    const headers = ['Year', 'COC %'];
    const data = results.coc.map(row => ({
      'Year': row.Year,
      'COC %': row.COC !== null ? formatPercent(row.COC) : 'N/A'
    }));
    addStyledTable(ws, data, headers);
  }

  // Sheet 5: Valuation
  if (results.valuation && results.valuation.length > 0) {
    const ws = workbook.addWorksheet('Valuation');
    const headers = ['Year', 'NOI', 'Sale Price', 'Remaining Balance', 'Equity'];
    const data = results.valuation.map(row => ({
      'Year': row.Year,
      'NOI': formatCurrency(row['NOI']),
      'Sale Price': formatCurrency(row['Sale Price']),
      'Remaining Balance': formatCurrency(row['Remaining Balance']),
      'Equity': formatCurrency(row['Equity'])
    }));
    addStyledTable(ws, data, headers);
  }

  // Sheet 6 (optional): Input Data
  if (includeInputs && formData) {
    const ws = workbook.addWorksheet('Input Data');
    const headers = ['Category', 'Field', 'Value'];
    const data = [];
    
    if (formData.property) {
      data.push({ Category: 'Property Details', Field: 'Purchase Price', Value: formatCurrency(formData.property.purchasePrice) });
      data.push({ Category: 'Property Details', Field: 'Square Footage', Value: String(formData.property.sqft) });
      data.push({ Category: 'Property Details', Field: 'Monthly Rent', Value: formatCurrency(formData.property.monthlyRent) });
    }
    
    if (formData.params) {
      data.push({ Category: 'Loan Terms', Field: 'LTV', Value: formatPercent(formData.params.loanAmount) });
      data.push({ Category: 'Loan Terms', Field: 'Interest Rate', Value: formatPercent(formData.params.interestRate) });
      data.push({ Category: 'Loan Terms', Field: 'Loan Term', Value: `${formData.params.loanTerm} years` });
      data.push({ Category: 'Loan Terms', Field: 'Amortization', Value: `${formData.params.amortizationPeriod} years` });
      data.push({ Category: 'Operating', Field: 'Vacancy', Value: formatPercent(formData.params.vacancy) });
      data.push({ Category: 'Operating', Field: 'Management Fee', Value: formatPercent(formData.params.managementFee) });
      data.push({ Category: 'Market', Field: 'Rent Increase', Value: formatPercent(formData.params.rentIncrease) });
      data.push({ Category: 'Market', Field: 'Expense Increase', Value: formatPercent(formData.params.expenseIncrease) });
    }
    
    if (formData.valuation) {
      data.push({ Category: 'Valuation', Field: 'Hold Period', Value: `${formData.valuation.holdPeriod} years` });
      data.push({ Category: 'Valuation', Field: 'Cap Rate', Value: formatPercent(formData.valuation.capRate) });
    }
    
    if (data.length > 0) {
      addStyledTable(ws, data, headers);
    }
  }

  // Download
  const filename = `PropNinja_Analysis_${getDateString()}.xlsx`;
  await downloadWorkbook(workbook, filename);
};

/**
 * Export ZIP Mode results to Excel
 * @param {Array} passedProperties - Array of passed property objects
 * @param {Object} params - The analysis parameters
 * @param {Object} screening - Screening thresholds (requiredCOC, cocTerm, holdPeriod)
 * @param {boolean} includeDetails - Whether to include per-property detail sheets
 * @param {boolean} includeInputs - Whether to include input data sheet
 * @param {string} propertyType - 'single_family' or 'multi_family'
 */
export const exportZipModeResults = async (passedProperties, params, screening, includeDetails = false, includeInputs = false, propertyType = 'single_family') => {
  if (!passedProperties || passedProperties.length === 0) {
    console.error('No properties to export');
    return;
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'PropNinja';
  workbook.created = new Date();

  // Sheet 1: Passed Properties Summary
  const summaryWs = workbook.addWorksheet('Passed Properties');
  let headers, summaryData;
  
  if (propertyType === 'multi_family') {
    headers = ['Address', 'Price', 'Units', 'Sqft', 'Total Rent', 'COC %'];
    summaryData = passedProperties.map(prop => ({
      'Address': prop.address || 'N/A',
      'Price': formatCurrency(prop.list_price),
      'Units': prop.unit_count || 'N/A',
      'Sqft': prop.sqft || 'N/A',
      'Total Rent': formatCurrency(prop.total_rent),
      'COC %': formatPercent(prop.coc_percentage_at_year)
    }));
  } else {
    headers = ['Address', 'Price', 'Beds', 'Baths', 'Sqft', 'COC %'];
    summaryData = passedProperties.map(prop => ({
      'Address': prop.address || 'N/A',
      'Price': formatCurrency(prop.list_price),
      'Beds': prop.beds || 'N/A',
      'Baths': prop.baths || 'N/A',
      'Sqft': prop.sqft || 'N/A',
      'COC %': formatPercent(prop.coc_percentage_at_year)
    }));
  }
  addStyledTable(summaryWs, summaryData, headers);

  // Helper to create short address for sheet name (Excel limit: 31 chars)
  const getShortAddress = (address, index) => {
    if (!address) return `Property ${index + 1}`;
    const parts = address.split(',')[0].trim();
    const shortened = parts
      .replace(/\s+(Street|Avenue|Boulevard|Drive|Road|Lane|Court|Circle|Way|Place)$/i, '')
      .replace(/\s+(St|Ave|Blvd|Dr|Rd|Ln|Ct|Cir|Wy|Pl)$/i, '');
    return shortened.substring(0, 31);
  };

  // Per-property detail sheets (optional)
  if (includeDetails) {
    const usedNames = new Set();
    passedProperties.forEach((prop, index) => {
      let sheetName = getShortAddress(prop.address, index);
      if (usedNames.has(sheetName)) {
        sheetName = `${sheetName.substring(0, 28)}_${index + 1}`;
      }
      usedNames.add(sheetName);
      
      const ws = workbook.addWorksheet(sheetName);
      let currentRow = 1;
      
      // Property header
      ws.getCell(currentRow, 1).value = 'Property Address:';
      ws.getCell(currentRow, 1).font = { bold: true };
      ws.getCell(currentRow, 2).value = prop.address || 'N/A';
      currentRow++;
      ws.getCell(currentRow, 1).value = 'List Price:';
      ws.getCell(currentRow, 1).font = { bold: true };
      ws.getCell(currentRow, 2).value = formatCurrency(prop.list_price);
      currentRow++;
      ws.getCell(currentRow, 1).value = 'COC %:';
      ws.getCell(currentRow, 1).font = { bold: true };
      ws.getCell(currentRow, 2).value = formatPercent(prop.coc_percentage_at_year);
      currentRow += 2;
      
      // Cash Flow
      if (prop.cash_flow && prop.cash_flow.length > 0) {
        ws.getCell(currentRow, 1).value = 'CASH FLOW';
        ws.getCell(currentRow, 1).font = { bold: true, size: 12 };
        currentRow++;
        const cfHeaders = ['Year', 'Gross Rev', 'Vacancy', 'EGR', 'Expenses', 'NOI', 'Principal', 'Interest', 'Cash Flow'];
        const cfData = prop.cash_flow.map(row => ({
          'Year': row.Year,
          'Gross Rev': formatCurrency(row['Potential Gross Revenue']),
          'Vacancy': formatCurrency(row['Vacancy Loss']),
          'EGR': formatCurrency(row['Effective Gross Revenue']),
          'Expenses': formatCurrency(row['Operating Expenses']),
          'NOI': formatCurrency(row['NOI']),
          'Principal': formatCurrency(row['Principal Payment']),
          'Interest': formatCurrency(row['Interest Payment']),
          'Cash Flow': formatCurrency(row['Cash Flow'])
        }));
        currentRow = addStyledTable(ws, cfData, cfHeaders, currentRow);
        currentRow++;
      }
      
      // Amortization
      if (prop.amortization && prop.amortization.length > 0) {
        ws.getCell(currentRow, 1).value = 'AMORTIZATION';
        ws.getCell(currentRow, 1).font = { bold: true, size: 12 };
        currentRow++;
        const amHeaders = ['Year', 'Principal', 'Interest', 'Total Payment', 'Balance'];
        const amData = prop.amortization.map(row => ({
          'Year': row.Year,
          'Principal': formatCurrency(row['Principal Payment']),
          'Interest': formatCurrency(row['Interest Payment']),
          'Total Payment': formatCurrency(row['Principal Payment'] + row['Interest Payment']),
          'Balance': formatCurrency(row['Remaining Balance'])
        }));
        currentRow = addStyledTable(ws, amData, amHeaders, currentRow);
        currentRow++;
      }
      
      // IRR
      if (prop.annual_irrs && prop.annual_irrs.length > 0) {
        ws.getCell(currentRow, 1).value = 'IRR';
        ws.getCell(currentRow, 1).font = { bold: true, size: 12 };
        currentRow++;
        const irrHeaders = ['Year', 'IRR'];
        const irrData = prop.annual_irrs.map(row => ({
          'Year': row.Year,
          'IRR': row.IRR !== null ? formatPercent(row.IRR) : 'N/A'
        }));
        currentRow = addStyledTable(ws, irrData, irrHeaders, currentRow);
        currentRow++;
      }
      
      // COC
      if (prop.coc && prop.coc.length > 0) {
        ws.getCell(currentRow, 1).value = 'COC';
        ws.getCell(currentRow, 1).font = { bold: true, size: 12 };
        currentRow++;
        const cocHeaders = ['Year', 'COC %'];
        const cocData = prop.coc.map(row => ({
          'Year': row.Year,
          'COC %': row.COC !== null ? formatPercent(row.COC) : 'N/A'
        }));
        currentRow = addStyledTable(ws, cocData, cocHeaders, currentRow);
        currentRow++;
      }
      
      // Valuation
      if (prop.valuation && prop.valuation.length > 0) {
        ws.getCell(currentRow, 1).value = 'VALUATION';
        ws.getCell(currentRow, 1).font = { bold: true, size: 12 };
        currentRow++;
        const valHeaders = ['Year', 'NOI', 'Sale Price', 'Balance', 'Equity'];
        const valData = prop.valuation.map(row => ({
          'Year': row.Year,
          'NOI': formatCurrency(row['NOI']),
          'Sale Price': formatCurrency(row['Sale Price']),
          'Balance': formatCurrency(row['Remaining Balance']),
          'Equity': formatCurrency(row['Equity'])
        }));
        addStyledTable(ws, valData, valHeaders, currentRow);
      }
      
      // Auto-fit first column
      ws.getColumn(1).width = 18;
      ws.getColumn(2).width = 15;
    });
  }

  // Input Data sheet (optional)
  if (includeInputs && params) {
    const ws = workbook.addWorksheet('Input Data');
    const inputHeaders = ['Category', 'Field', 'Value'];
    const inputData = [];
    
    if (screening) {
      inputData.push({ Category: 'Screening', Field: 'Required COC %', Value: formatPercent(screening.requiredCOC) });
      inputData.push({ Category: 'Screening', Field: 'COC Target Year', Value: `Year ${screening.cocTerm}` });
      inputData.push({ Category: 'Screening', Field: 'Hold Period', Value: `${screening.holdPeriod} years` });
    }
    
    inputData.push({ Category: 'Loan Terms', Field: 'LTV', Value: formatPercent(params.loanAmount) });
    inputData.push({ Category: 'Loan Terms', Field: 'Interest Rate', Value: formatPercent(params.interestRate) });
    inputData.push({ Category: 'Loan Terms', Field: 'Loan Term', Value: `${params.loanTerm} years` });
    inputData.push({ Category: 'Loan Terms', Field: 'Amortization', Value: `${params.amortizationPeriod} years` });
    inputData.push({ Category: 'Operating', Field: 'Vacancy', Value: formatPercent(params.vacancy) });
    inputData.push({ Category: 'Operating', Field: 'Management Fee', Value: formatPercent(params.managementFee) });
    inputData.push({ Category: 'Market', Field: 'Rent Increase', Value: formatPercent(params.rentIncrease) });
    inputData.push({ Category: 'Market', Field: 'Expense Increase', Value: formatPercent(params.expenseIncrease) });
    
    addStyledTable(ws, inputData, inputHeaders);
  }

  // Download
  const filename = `PropNinja_ZipScreening_${getDateString()}.xlsx`;
  await downloadWorkbook(workbook, filename);
};
